import UserServices from '@/services/users-services';
import type {
  StorageUsage,
  UsageLimit,
  UserUsageData,
} from '@/types/users-types';
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import type { RootState } from '../store';

type UsageLimitType = 'searches' | 'chat_queries';

// Define the usage state interface
interface UsageState {
  usageData: UserUsageData | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null; // timestamp of last successful fetch
}

// Initial state
const initialState: UsageState = {
  usageData: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

// Async thunk to fetch user usage data
export const fetchUserUsage = createAsyncThunk(
  'usage/fetchUserUsage',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await UserServices.getUserUsage();

      if (response.status === 'success' && response.data) {
        return response.data;
      } else {
        return rejectWithValue(
          response.message || 'Failed to fetch usage data'
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while fetching usage data';
      return rejectWithValue(errorMessage);
    }
  }
);

// Usage slice
const usageSlice = createSlice({
  name: 'usage',
  initialState,
  reducers: {
    // Set usage data directly (for optimistic updates)
    setUsageData: (state, action: PayloadAction<UserUsageData>) => {
      state.usageData = action.payload;
      state.error = null;
      state.lastFetched = Date.now();
    },

    // Clear usage data
    clearUsageData: (state) => {
      state.usageData = null;
      state.error = null;
      state.lastFetched = null;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Update a specific usage limit (searches or chat_queries)
    updateUsageLimit: (
      state,
      action: PayloadAction<{ type: UsageLimitType; limit: UsageLimit }>
    ) => {
      if (!state.usageData) return;
      state.usageData[action.payload.type] = action.payload.limit;
    },

    // Increment consumed count for a usage type (optimistic update after action)
    incrementUsage: (
      state,
      action: PayloadAction<{ type: UsageLimitType; amount?: number }>
    ) => {
      if (!state.usageData) return;
      const { type, amount = 1 } = action.payload;
      const current = state.usageData[type];
      const newConsumed = current.consumed + amount;
      // Cap at limit if one exists, otherwise allow unlimited growth
      state.usageData[type] = {
        ...current,
        consumed:
          current.limit !== null
            ? Math.min(newConsumed, current.limit)
            : newConsumed,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserUsage
      .addCase(fetchUserUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.usageData = action.payload;
        state.error = null;
        state.lastFetched = Date.now();
      })
      .addCase(fetchUserUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Set lastFetched to prevent UsageInitializer from re-dispatching
        // in an infinite loop when the API returns non-200 responses.
        // Manual retry is still possible via onRetry in SidebarUsageInfo.
        state.lastFetched = Date.now();
      });
  },
});

// Selectors
export const selectUsageData = (state: RootState) => state.usage.usageData;
export const selectIsUsageLoading = (state: RootState) => state.usage.isLoading;
export const selectUsageError = (state: RootState) => state.usage.error;
export const selectLastFetched = (state: RootState) => state.usage.lastFetched;

// =============================================
// Zadanie-based selectors (v2 quota system)
// =============================================

/** Whether the user is a paid subscriber (zadanie-aware, falls back to legacy). */
export const selectIsZadaniePaidUser = (state: RootState): boolean => {
  const zadanie = state.usage.usageData?.zadanie;
  if (!zadanie) return state.usage.usageData?.is_paid_user ?? false;
  return (
    zadanie.plan.plan_type === 'individual' ||
    zadanie.plan.name.toLowerCase() !== 'trial'
  );
};

/**
 * Whether the user's research (chat) quota is FULLY exhausted — no usable mode
 * left at all, not even standard/basic.
 *
 * The monthly soft cap and the daily full/deep limits only gate the *advanced*
 * modes (full + deep) — when those are exhausted the user can still run Standard
 * (basic search + fast chat), so the UI must NOT be blocked. We therefore key
 * exhaustion off Standard availability alone: reached only when the plan defines
 * a finite daily Standard allowance AND it is used up. For every current plan
 * `daily_standard_limit` is NULL (unlimited) so `standard_remaining` is NULL and
 * this is `false` — but a future finite-Standard plan would surface correctly.
 *
 * Trial expiry is deliberately NOT a factor (#5485): expired-trial free users get
 * Standard mode, so they are not "fully exhausted".
 *
 * Falls back to legacy `selectIsChatQueryLimitReached` when zadanie is absent.
 */
export const selectIsZadanieLimitReached = (state: RootState): boolean => {
  const zadanie = state.usage.usageData?.zadanie;
  if (!zadanie) {
    // Legacy fallback — mirror selectIsChatQueryLimitReached.
    const { usageData } = state.usage;
    if (
      !usageData ||
      usageData.chat_queries.limit === null ||
      usageData.chat_queries.limit === 0
    )
      return false;
    return usageData.chat_queries.consumed >= usageData.chat_queries.limit;
  }
  // Standard "reached" only when a finite daily Standard allowance is used up.
  // null remaining = unlimited Standard = never reached.
  return (
    zadanie.daily.standard_remaining !== null &&
    zadanie.daily.standard_remaining <= 0
  );
};

/**
 * Whether the user's search quota is exhausted.
 *
 * Search consumes "standard" mode (#5483), which is NOT subject to the monthly
 * soft cap — the cap only gates full/deep. So `monthly.remaining` is the WRONG
 * signal here (it would block search the moment the advanced-mode cap is hit,
 * even though Standard search is still available). Key off Standard availability
 * instead: reached only when the plan defines a finite daily Standard allowance
 * AND it is used up. For every current plan `daily_standard_limit` is NULL
 * (unlimited) so this is `false` and the search button stays enabled.
 *
 * Trial expiry is deliberately NOT a factor (#5485): expired-trial free users
 * keep Standard search.
 *
 * Falls back to legacy `selectIsSearchesLimitReached` when zadanie is absent.
 */
export const selectIsSearchLimitReached = (state: RootState): boolean => {
  const zadanie = state.usage.usageData?.zadanie;
  if (!zadanie) {
    // Legacy fallback — mirror selectIsSearchesLimitReached.
    const { usageData } = state.usage;
    if (
      !usageData ||
      usageData.searches.limit === null ||
      usageData.searches.limit === 0
    )
      return false;
    return usageData.searches.consumed >= usageData.searches.limit;
  }
  // Standard "reached" only when a finite daily Standard allowance is used up.
  // null remaining = unlimited Standard = never reached (NOT monthly-capped).
  return (
    zadanie.daily.standard_remaining !== null &&
    zadanie.daily.standard_remaining <= 0
  );
};

// =============================================
// Legacy selectors (kept for backward compat)
// =============================================

// Selector to check if any usage limit is reached
export const selectIsAnyLimitReached = (state: RootState) => {
  const { usageData } = state.usage;
  if (!usageData) return false;

  // null limit means unlimited, skip that check
  const chatQueryLimitReached =
    usageData.chat_queries.limit !== null &&
    usageData.chat_queries.limit > 0 &&
    usageData.chat_queries.consumed >= usageData.chat_queries.limit;

  const searchesLimitReached =
    usageData.searches.limit !== null &&
    usageData.searches.limit > 0 &&
    usageData.searches.consumed >= usageData.searches.limit;

  return chatQueryLimitReached || searchesLimitReached;
};

// Selector to check if AI query limit is reached
export const selectIsChatQueryLimitReached = (state: RootState) => {
  const { usageData } = state.usage;
  // null limit means unlimited (paid users), 0 means not loaded
  if (
    !usageData ||
    usageData.chat_queries.limit === null ||
    usageData.chat_queries.limit === 0
  )
    return false;
  return usageData.chat_queries.consumed >= usageData.chat_queries.limit;
};

// Selector to check if searches limit is reached
export const selectIsSearchesLimitReached = (state: RootState) => {
  const { usageData } = state.usage;
  // null limit means unlimited (paid users), 0 means not loaded
  if (
    !usageData ||
    usageData.searches.limit === null ||
    usageData.searches.limit === 0
  )
    return false;
  return usageData.searches.consumed >= usageData.searches.limit;
};

// Storage selectors
export const selectStorageUsage = (state: RootState): StorageUsage | null =>
  state.usage.usageData?.storage ?? null;

// Selector to check if user is a paid user
export const selectIsPaidUser = (state: RootState) =>
  state.usage.usageData?.is_paid_user ?? false;

export default usageSlice.reducer;
